import React from 'react';
import { Link, Navigate } from 'react-router-dom';
import { useLanguage } from '../contexts/LanguageContext';
import { Card } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { ArrowLeft, PlayCircle, Video } from 'lucide-react';
import { VIDEOS } from '../data/videos';
import { VideoPlayer } from '../components/VideoPlayer';
import { useTestsCompleted } from '../hooks/useTestsCompleted';

const VideoCard = ({ video, language, index }) => (
  <Card className="glass-card rounded-3xl overflow-hidden border-0 shadow-lg animate-fade-in-up" style={{ animationDelay: `${index * 80}ms` }} data-testid={`video-card-${video.id}`}>
    <div className="aspect-video bg-black">
      {video.src ? (
        <VideoPlayer video={video} className="w-full h-full" testId={`video-player-${video.id}`} />
      ) : (
        <iframe
          className="w-full h-full"
          src={`https://www.youtube-nocookie.com/embed/${video.youtubeId}`}
          title={language === 'fr' ? video.title_fr : video.title_en}
          allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
          allowFullScreen
        />
      )}
    </div>
    <div className="p-5">
      <h3 className="font-bold text-gray-900 mb-1">{language === 'fr' ? video.title_fr : video.title_en}</h3>
      {(video.description_fr || video.description_en) && (
        <p className="text-sm text-gray-600">{language === 'fr' ? video.description_fr : video.description_en}</p>
      )}
    </div>
  </Card>
);

export const Videos = () => {
  const { language } = useLanguage();
  const { loading, allDone } = useTestsCompleted();

  if (loading) {
    return <div className="min-h-[60vh] flex items-center justify-center"><div className="w-10 h-10 border-4 border-primary border-t-transparent rounded-full animate-spin" /></div>;
  }
  if (!allDone) return <Navigate to="/dashboard" replace />;

  return (
    <div className="min-h-[calc(100vh-4rem)] bg-[#F9FAFB] py-8 sm:py-12" data-testid="videos-page">
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
        <Link to="/dashboard">
          <Button variant="ghost" className="rounded-full mb-6" data-testid="videos-back-btn">
            <ArrowLeft className="mr-2 w-4 h-4" />
            {language === 'fr' ? 'Tableau de bord' : 'Dashboard'}
          </Button>
        </Link>
        <h1 className="text-3xl sm:text-4xl font-bold text-gray-900 mb-2 flex items-center gap-3">
          <Video className="w-8 h-8 text-red-600" />
          {language === 'fr' ? 'Vidéos' : 'Videos'}
        </h1>
        <p className="text-gray-600 mb-8">
          {language === 'fr' ? 'Des conseils en vidéo pour vous accompagner dans votre arrêt.' : 'Video tips to support you as you quit.'}
        </p>
        {VIDEOS.length === 0 ? (
          <Card className="glass-card rounded-3xl border-0 shadow-lg p-12 text-center" data-testid="videos-empty">
            <PlayCircle className="w-14 h-14 mx-auto text-red-400 mb-4" />
            <h3 className="text-lg font-bold text-gray-900 mb-1">{language === 'fr' ? 'Bientôt disponible' : 'Coming soon'}</h3>
            <p className="text-sm text-gray-600">{language === 'fr' ? 'Les premières vidéos arrivent très vite.' : 'The first videos are coming very soon.'}</p>
          </Card>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {VIDEOS.map((v, i) => <VideoCard key={v.id} video={v} language={language} index={i} />)}
          </div>
        )}
      </div>
    </div>
  );
};
